import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { color } from '../tokens';

// First-render gate. The AI proposes stages / taxonomy; the consultant confirms or
// edits BEFORE the tab renders — never discovers a misalignment mid screen-share.
// No backdrop-dismiss and no close affordance: the only ways out are "use these" or
// "edit before continuing".
interface ProposalModalProps {
  open: boolean;
  title: string;
  subtitle: string;
  children: ReactNode;
  onUse: () => void;
  onEdit: () => void;
}

export default function ProposalModal({ open, title, subtitle, children, onUse, onEdit }: ProposalModalProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'absolute', inset: 0, zIndex: 20,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 20px', overflowY: 'auto',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 560, background: color.surface,
        border: `1px solid ${color.hairline}`, borderRadius: 16,
        boxShadow: '0 24px 60px rgba(15,23,42,0.28)', overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 22px 16px', borderBottom: `1px solid ${color.hairline}` }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 700,
            letterSpacing: '0.06em', color: color.purple, background: color.pillActiveBg,
            padding: '3px 9px', borderRadius: 6,
          }}>
            <Sparkles size={12} /> AI PROPOSED
          </span>
          <h3 style={{ margin: '12px 0 0', fontSize: 18, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            {title}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted, lineHeight: 1.5 }}>{subtitle}</p>
        </div>

        <div style={{ padding: '16px 22px', maxHeight: '46vh', overflowY: 'auto' }}>
          {children}
        </div>

        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          padding: '14px 22px', borderTop: `1px solid ${color.hairline}`, background: color.surfaceAlt,
        }}>
          <button
            onClick={onEdit}
            style={{
              fontSize: 13, padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
              background: color.surface, color: color.text, border: `1px solid ${color.hairline}`,
            }}
          >
            Edit before continuing
          </button>
          <button
            onClick={onUse}
            style={{
              fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
              background: color.ink, color: '#fff', border: 'none',
            }}
          >
            Use these
          </button>
        </div>
      </div>
    </div>
  );
}
